import { apiClient } from './axios';

import type {
  CallWithContact,
  CallQuery,
  HumanReviewData,
  PaginatedResponse,
} from '@repo/shared-types';

export interface ManualEmailRecord {
  id: string;
  call_id: string;
  sent_by: string;
  outcome: string;
  sent_at: string;
  user: { id: string; name: string };
}

export interface WorkflowExecutionLog {
  started_at: string;
  completed_at: string;
  total_nodes: number;
  executed_nodes: number;
  errors: number;
  dry_run: boolean;
  results: Array<{
    nodeId: string;
    nodeType: string;
    status: string;
    message?: string;
    branch?: string;
    duration_ms?: number;
    request_payload?: unknown;
    response_data?: unknown;
  }>;
}

export interface SentSmsRecord {
  id: string;
  phone_number: string;
  message_type: string;
  message_body: string;
  twilio_sid: string | null;
  sent_at: string;
}

export interface CallTrackingItem {
  id: string;
  trackingId: string;
  callId: string;
  linkType: string;
  originalUrl: string;
  clicked: boolean;
  clickedAt: string | null;
  createdAt: string;
}

export interface CallTracking {
  hasTracking: boolean;
  links: CallTrackingItem[];
}

export interface ManualHandoffResponse {
  callId: string;
  callSid: string;
  status: 'operator_takeover_active';
}

export interface ManualControlResponse {
  callId: string;
  callSid: string;
  status: 'returned_to_ai' | 'hangup_requested';
}

/**
 * Availability totals for the dashboard. `source` says where they came from:
 * 'tool' counts only what was uploaded to the client's booking system,
 * 'review' counts what the review agent extracted from the conversation.
 */
export interface AvailabilityHoursStats {
  totalHours: number;
  totalShifts: number;
  totalCalls: number;
  source: 'tool' | 'review';
}

export const callsApi = {
  getCalls: async (query?: Partial<CallQuery>): Promise<PaginatedResponse<CallWithContact>> => {
    const response = await apiClient.get<PaginatedResponse<CallWithContact>>('/calls', {
      params: query,
    });
    return response.data;
  },

  getCall: async (id: string): Promise<CallWithContact> => {
    const response = await apiClient.get<CallWithContact>(`/calls/${id}`);
    return response.data;
  },

  getCallTracking: async (id: string): Promise<CallTracking> => {
    const response = await apiClient.get<CallTracking>(`/calls/${id}/tracking`);
    return response.data;
  },

  deleteCall: async (id: string): Promise<void> => {
    await apiClient.delete(`/calls/${id}`);
  },

  getAvailabilityHoursStats: async (): Promise<AvailabilityHoursStats> => {
    const response = await apiClient.get<AvailabilityHoursStats>('/calls/stats/availability-hours');
    return response.data;
  },

  updateHumanReviewed: async (
    id: string,
    humanReviewed: boolean,
  ): Promise<{ id: string; human_reviewed: boolean }> => {
    const response = await apiClient.patch<{ id: string; human_reviewed: boolean }>(
      `/calls/${id}/human-reviewed`,
      { human_reviewed: humanReviewed },
    );
    return response.data;
  },

  getHumanReview: async (id: string): Promise<HumanReviewData | null> => {
    const response = await apiClient.get<HumanReviewData | null>(`/calls/${id}/human-review`);
    return response.data;
  },

  upsertHumanReview: async (
    id: string,
    data: {
      outcome?: string | null;
      availability_data?: Record<string, unknown> | null;
      lead_data?: Record<string, unknown> | null;
    },
  ): Promise<HumanReviewData> => {
    const response = await apiClient.put<HumanReviewData>(`/calls/${id}/human-review`, data);
    return response.data;
  },

  sendManualEmail: async (id: string): Promise<ManualEmailRecord> => {
    const response = await apiClient.post<ManualEmailRecord>(
      `/calls/${id}/send-notification`,
      undefined,
      {
        timeout: 30000,
      },
    );
    return response.data;
  },

  getManualEmails: async (id: string): Promise<ManualEmailRecord[]> => {
    const response = await apiClient.get<ManualEmailRecord[]>(`/calls/${id}/manual-emails`);
    return response.data;
  },

  getWorkflowLog: async (id: string): Promise<WorkflowExecutionLog | null> => {
    const response = await apiClient.get<WorkflowExecutionLog | null>(`/calls/${id}/workflow-log`);
    return response.data;
  },

  getSentSms: async (id: string): Promise<SentSmsRecord[]> => {
    const response = await apiClient.get<SentSmsRecord[]>(`/calls/${id}/sent-sms`);
    return response.data;
  },

  manualHandoffCall: async (id: string): Promise<ManualHandoffResponse> => {
    const response = await apiClient.post<ManualHandoffResponse>(`/twilio/manual-handoff/${id}`);
    return response.data;
  },

  runClassifier: async (
    id: string,
    opts?: { classifierConfigId?: string; name?: string },
  ): Promise<{
    callId: string;
    classifierConfigId: string;
    taxonomyVersion: string;
    labels: Record<string, boolean>;
    data: Record<string, unknown>;
    confidenceScore?: number;
  }> => {
    const response = await apiClient.post(`/calls/${id}/classify`, opts ?? {}, {
      timeout: 120000,
    });
    return response.data;
  },

  runQuality: async (
    id: string,
    opts?: { qualityConfigId?: string; name?: string },
  ): Promise<{
    callId: string;
    qualityConfigId: string;
    rubricVersion: string;
    flags: Record<string, boolean>;
    data: Record<string, unknown>;
    reasoning: string | null;
    confidenceScore?: number;
  }> => {
    const response = await apiClient.post(`/calls/${id}/assess-quality`, opts ?? {}, {
      timeout: 180000,
    });
    return response.data;
  },

  updateClassificationLabels: async (
    id: string,
    labels: Record<string, boolean>,
    name?: string,
  ) => {
    const response = await apiClient.patch(`/calls/${id}/classification/labels`, {
      labels,
      ...(name ? { name } : {}),
    });
    return response.data;
  },

  updateQualityFlags: async (id: string, flags: Record<string, boolean>, name?: string) => {
    const response = await apiClient.patch(`/calls/${id}/quality/flags`, {
      flags,
      ...(name ? { name } : {}),
    });
    return response.data;
  },

  /**
   * Returns every named classification + quality run for the call. The
   * canonical runs ('classification' / 'quality') are exposed through
   * call.classification / call.quality on the regular detail endpoint;
   * this one exposes the full set including any sibling named runs.
   */
  listAgentRuns: async (
    id: string,
  ): Promise<{
    classifications: Array<{
      call_id: string;
      name: string;
      // null when written by an inline `agent` workflow node (no config row).
      classifier_config_id: string | null;
      classifier_config: { id: string; name: string } | null;
      taxonomy_version: string;
      labels: Record<string, unknown>;
      data: Record<string, unknown>;
      confidence_score: number | null;
      classified_at: string;
    }>;
    qualities: Array<{
      call_id: string;
      name: string;
      // null when written by an inline `agent` workflow node (no config row).
      quality_config_id: string | null;
      quality_config: { id: string; name: string } | null;
      rubric_version: string;
      flags: Record<string, unknown>;
      data: Record<string, unknown>;
      reasoning: string | null;
      confidence_score: number | null;
      assessed_at: string;
    }>;
  }> => {
    const response = await apiClient.get(`/calls/${id}/agent-runs`);
    return response.data;
  },

  manualReturnToAi: async (id: string): Promise<ManualControlResponse> => {
    const response = await apiClient.post<ManualControlResponse>(
      `/twilio/manual-return-to-ai/${id}`,
    );
    return response.data;
  },

  manualHangupCall: async (id: string): Promise<ManualControlResponse> => {
    const response = await apiClient.post<ManualControlResponse>(`/twilio/manual-hangup/${id}`);
    return response.data;
  },
};
